import type { TFunction } from "i18next";
import { ContractNature, Currency, FoaPart, Regulation, type SourcingEventDto } from "../../autogen/bff-api";
import {
  FOA_NATIONAL_THRESHOLD_NOK,
  FOA_SPECIAL_SERVICES_EEA_THRESHOLD_NOK,
  foaPartBanner,
  formatThresholdNok,
  needsBuyerLegalType,
  OUTSIDE_LOA_THRESHOLD_NOK,
  UTILITIES_SPECIAL_SERVICES_EEA_THRESHOLD_NOK,
  UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK,
  valueIsFoaPartI,
  valueIsUtilitiesPartIWhateverIsBought,
} from "../procurement/foaPartDisplay";
import { minTenderDays, tenderDeadlineProvision } from "../procurement/karens";
import { earliestTenderDeadline } from "../procurement/earliestTenderDeadline";
import { isDpsCompetition, isTwoStageProcedure } from "../procurement/procedure";
import { allowedProcedures, type AllowedProcedure, type ProcedureInputs } from "../procurement/procedureAvailability";
import { MODEL_LANGUAGE } from "./tools";
import { procedureTileKey } from "../procurement/publicProcurementOptions";

export const PROCUREMENT_RULES_TOOL_NAME = "get_procurement_rules";

export type MissingInput = "estimatedValue" | "regulation" | "contractNature" | "buyerLegalType" | "classification";

export type SectorRule = { topic: string; rule: string; legalBasis: string; note: string };

export type ProcurementRules = (
  | { kind: "private"; part?: FoaPart; note: string }
  | { kind: "miniCompetition"; note: string }
  | {
      kind: "dpsCompetition";
      regulation: string | null;
      part: string | null;
      minimumTenderDays: number | null;
      legalBasis: string | null;
      note: string;
    }
  | { kind: "unclassified"; missing: MissingInput[]; note: string }
  | {
      kind: "public";
      regulation: string | null;
      part: string | null;
      classification: string | null;
      missing: MissingInput[];
      procedures: ProcedureRule[];
      tenderDeadlineNote: string;
    }
) & { sectorRules?: SectorRule[] };

export type ProcedureRule = AllowedProcedure & { name: string; earliestTenderDeadline: string | null };

const TENDER_DEADLINE_NOTE =
  "earliestTenderDeadline er den tidligste tilbudsfristen systemet godtar for prosedyren: minstefristen regnet fra " +
  "kunngjøringen i fremdriftsplanen eller fra nå, det som er senest, kl. 23:59. Ber brukeren om minstefristen eller " +
  "den korteste fristen, bruk denne verdien uendret som eventData.bidSubmissionDeadline, og regn den aldri ut selv. " +
  "Er den null, har prosedyren ingen fast minstefrist å regne fra.";

export const procurementRulesFor = (
  event: SourcingEventDto,
  t: TFunction,
  now: Date = new Date(),
  buyerRunsPublicProcurements = false,
  buyerRegulation: Regulation | null = null,
): ProcurementRules => {
  const rules = rulesForContext(event, t, now, buyerRunsPublicProcurements, buyerRegulation);
  const sectorRules = sectorRulesFor(event);
  return sectorRules.length > 0 ? { ...rules, sectorRules } : rules;
};

const CLEANING_SERVICES: SectorRule = {
  topic: "Renhold",
  rule:
    "Kjøp av renholdstjeneste er kun tillatt fra virksomhet som enten er godkjent, har søknad om godkjenning til " +
    "behandling eller har HMS-kortbestilling til behandling.",
  legalBasis:
    "Forskrift om offentlig godkjenning av renholdsvirksomheter og om kjøp av renholdstjenester " +
    "(FOR-2012-05-08-408) § 17",
  note:
    "Gjelder alle som kjøper renholdstjenester, offentlige og private. Ta det med som kvalifikasjonskrav i hvert " +
    "alternativ som setter kvalifikasjonskrav, formulert slik § 17 gjør: leverandøren er godkjent av Arbeidstilsynet, " +
    "eller har søknad om godkjenning til behandling, eller har HMS-kortbestilling til behandling. Dette, og ikke mer, " +
    "er det forskriften krever: si at det følger av forskriften, og be aldri brukeren sjekke det med juridisk " +
    "rådgiver. Vil brukeren kreve mer, f.eks. at søknaden allerede er godkjent, er det oppdragsgiverens eget valg, " +
    "ikke loven, og kravet må stå i forhold til leveransen.",
};

const sectorRulesFor = (event: SourcingEventDto): SectorRule[] => (buysCleaning(event) ? [CLEANING_SERVICES] : []);

const buysCleaning = (event: SourcingEventDto): boolean => {
  const context = event.eventContext;
  const cpvCodes = context.type === "Public" ? (context.cpvCodes ?? []) : [];
  if (cpvCodes.some(isCleaningCpv)) return true;
  const nature = context.type === "Public" || context.type === "Unclassified" ? context.contractNature : undefined;
  const mayBeAService = nature !== ContractNature.Supplies && nature !== ContractNature.Works;
  return mayBeAService && /renhold/i.test(event.eventData.title ?? "");
};

const isCleaningCpv = (code: string): boolean => code.startsWith("9091") || code.startsWith("90900000");

const rulesForContext = (
  event: SourcingEventDto,
  t: TFunction,
  now: Date,
  buyerRunsPublicProcurements: boolean,
  buyerRegulation: Regulation | null,
): ProcurementRules => {
  const context = event.eventContext;
  switch (context.type) {
    case "Private":
      return buyerRunsPublicProcurements
        ? publicBuyersPrivateCompetitionRules(event, buyerRegulation)
        : PRIVATE_BUYER_RULES;
    case "MiniCompetition":
      return {
        kind: "miniCompetition",
        note:
          "En minikonkurranse følger prosedyren i rammeavtalen den kjøres under, så det er ikke noe eget prosedyrevalg. " +
          "Tilbudsfristen setter innkjøperen selv for hver minikonkurranse, lang nok for kontraktens kompleksitet og tiden " +
          "leverandørene trenger for å levere. Det er ingen fast lovbestemt minstefrist å oppgi.",
      };
    case "Unclassified": {
      const missing = missingBeforeClassification(context);
      return { kind: "unclassified", missing, note: unclassifiedNote(missing) };
    }
    case "Public": {
      if (isDpsCompetition(context.procedureType)) return dpsCompetitionRules(context);
      const inputs: ProcedureInputs = {
        regulation: context.regulation,
        foaPart: context.foaPart,
        contractNature: context.contractNature,
        serviceCategory: context.serviceCategory,
        estimatedValueNok: context.estimatedValue?.value,
      };
      const missing = missingInputs(context);
      const banner = foaPartBanner({ ...inputs, buyerLegalType: context.buyerLegalType });
      return {
        kind: "public",
        regulation: context.regulation ?? null,
        part: context.foaPart ?? null,
        classification: context.foaPart && banner ? String(t(banner.messageKey, { ...banner.values })) : null,
        missing,
        procedures: context.foaPart
          ? allowedProcedures(inputs, t).map((procedure) => withNameAndEarliestTenderDeadline(procedure, t, event, now))
          : [],
        tenderDeadlineNote: TENDER_DEADLINE_NOTE,
      };
    }
  }
};

const PRIVATE_BUYER_RULES: ProcurementRules = {
  kind: "private",
  note: "En privat anskaffelse følger ikke anskaffelsesregelverket, så det er ingen regelverksdel, prosedyrehjemmel eller lovbestemt minstefrist.",
};

const kr = (nok: number) => `${formatThresholdNok(nok)} kr ekskl. mva.`;

const NEVER_OUTSIDE_BECAUSE_PRIVATE =
  "Si aldri at anskaffelsen er utenfor eller ikke følger regelverket bare fordi konkurransen er privat.";

const ABOVE_FOA_PART_I =
  `Anskaffelsen hører da ikke til Del I, men til Del II av anskaffelsesforskriften (FOA § 5-1 (2)), og fra ` +
  "EØS-terskelverdien i FOA § 5-3 (1) til Del III (FOA § 5-1 (3)). Reglene for den delen gjelder, med prosedyrene " +
  "og minstefristene der, og anskaffelsen skal kunngjøres på Doffin (FOA § 8-17 (1)). Unntaket er helse-, sosial- " +
  `og undervisningstjenester, som er Del I under ${kr(FOA_SPECIAL_SERVICES_EEA_THRESHOLD_NOK)} (FOA § 5-1 (2) a).`;

const ABOVE_UTILITIES_PART_I =
  "Om den er Del I eller ikke, avgjør EØS-terskelverdien for det som kjøpes (forsyningsforskriften § 5-2): " +
  `${kr(UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK)} for varer og tjenester, høyere for bygge- og anleggskontrakter, ` +
  `og ${kr(UTILITIES_SPECIAL_SERVICES_EEA_THRESHOLD_NOK)} for særlige tjenester og helse- og sosialtjenester. Fra ` +
  "terskelverdien gjelder Del II (Del III for de særlige tjenestene), med reglene og minstefristene der og " +
  "kunngjøring på Doffin og TED.";

const bandsFor = (regulation: Regulation | null | undefined): string => {
  const floor = kr(OUTSIDE_LOA_THRESHOLD_NOK);
  const outside = `Under ${floor} gjelder verken anskaffelsesloven eller forskriftene (LOA § 2).`;
  if (regulation === Regulation.Foa) {
    return (
      `${outside} Fra ${floor} og under ${kr(FOA_NATIONAL_THRESHOLD_NOK)} gjelder Del I av anskaffelsesforskriften ` +
      `(FOA § 5-1 (1)). Fra ${kr(FOA_NATIONAL_THRESHOLD_NOK)} gjelder Del II, og fra EØS-terskelverdien Del III.`
    );
  }
  if (regulation === Regulation.Forsyningsforskriften) {
    return (
      `${outside} Fra ${floor} gjelder forsyningsforskriften (§ 1-1), og under ` +
      `${kr(UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK)} bare Del I. ${ABOVE_UTILITIES_PART_I}`
    );
  }
  return `${outside} Over det avgjør verdien og forskriften oppdragsgiveren følger hvilken del som gjelder.`;
};

const publicBuyersPrivateCompetitionRules = (
  event: SourcingEventDto,
  regulation: Regulation | null | undefined,
): ProcurementRules => {
  const estimate = event.eventContext.estimatedValue;
  const runAs =
    "Oppdragsgiveren er offentlig, og den private konkurransen er bare måten SourceMagnet gjennomfører " +
    "anskaffelsen på. Om anskaffelsesregelverket gjelder, og hvilken del, avgjør den estimerte verdien, ikke at " +
    "konkurransen er privat.";
  if (estimate?.value == null || estimate.currency !== Currency.Nok) {
    return {
      kind: "private",
      note: `${runAs} Verdien i kroner er ikke satt ennå. ${bandsFor(regulation)} ${NEVER_OUTSIDE_BECAUSE_PRIVATE}`,
    };
  }
  if (event.outsideProcurementAct) {
    return {
      kind: "private",
      note:
        `${runAs} Den estimerte verdien er under ${kr(OUTSIDE_LOA_THRESHOLD_NOK)}, så verken anskaffelsesloven eller ` +
        "forskriftene gjelder (LOA § 2). Det er ingen prosedyrehjemmel eller lovbestemt minstefrist.",
    };
  }
  const value = Number(estimate.value);
  if (valueIsFoaPartI(regulation, value)) {
    return {
      kind: "private",
      part: FoaPart.PartI,
      note:
        `${runAs} Den estimerte verdien er minst ${kr(OUTSIDE_LOA_THRESHOLD_NOK)} og under ` +
        `${kr(FOA_NATIONAL_THRESHOLD_NOK)}, så Del I av anskaffelsesforskriften gjelder (FOA § 5-1 (1)). Del I har ` +
        `ingen fast prosedyre og ingen lovbestemt minstefrist. ${NEVER_OUTSIDE_BECAUSE_PRIVATE}`,
    };
  }
  if (regulation === Regulation.Foa) {
    return {
      kind: "private",
      note:
        `${runAs} Den estimerte verdien er minst ${kr(FOA_NATIONAL_THRESHOLD_NOK)}, den nasjonale terskelverdien. ` +
        `${ABOVE_FOA_PART_I} ${NEVER_OUTSIDE_BECAUSE_PRIVATE}`,
    };
  }
  if (valueIsUtilitiesPartIWhateverIsBought(regulation, value)) {
    return {
      kind: "private",
      part: FoaPart.PartI,
      note:
        `${runAs} Oppdragsgiveren følger forsyningsforskriften, ikke anskaffelsesforskriften. Den estimerte verdien er ` +
        `minst ${kr(OUTSIDE_LOA_THRESHOLD_NOK)} og under ${kr(UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK)}, så ` +
        "Del I av forsyningsforskriften gjelder (forsyningsforskriften § 1-1 og § 5-2). Del I har ingen fast prosedyre " +
        `og ingen lovbestemt minstefrist. ${NEVER_OUTSIDE_BECAUSE_PRIVATE}`,
    };
  }
  if (regulation === Regulation.Forsyningsforskriften) {
    return {
      kind: "private",
      note:
        `${runAs} Oppdragsgiveren følger forsyningsforskriften, ikke anskaffelsesforskriften. Den estimerte verdien er ` +
        `minst ${kr(UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK)}. ${ABOVE_UTILITIES_PART_I} ` +
        NEVER_OUTSIDE_BECAUSE_PRIVATE,
    };
  }
  return {
    kind: "private",
    note:
      `${runAs} Den estimerte verdien er minst ${kr(OUTSIDE_LOA_THRESHOLD_NOK)}, så regelverket gjelder, men ` +
      `organisasjonen har ikke valgt hvilken forskrift den følger. ${bandsFor(regulation)} ` +
      NEVER_OUTSIDE_BECAUSE_PRIVATE,
  };
};

const withNameAndEarliestTenderDeadline = (
  procedure: AllowedProcedure,
  t: TFunction,
  event: SourcingEventDto,
  now: Date,
): ProcedureRule => ({
  ...procedure,
  name: String(t(procedureTileKey(procedure.procedureType), { lng: MODEL_LANGUAGE })),
  earliestTenderDeadline:
    procedure.minimumTenderDays != null && !isTwoStageProcedure(procedure.procedureType)
      ? earliestTenderDeadline({
          minimumDays: procedure.minimumTenderDays,
          announcementDate: event.eventData.milestonePlan?.announcementDate,
          now,
        })
      : null,
});

const dpsCompetitionRules = (
  context: Extract<SourcingEventDto["eventContext"], { type: "Public" }>,
): ProcurementRules => {
  const days = minTenderDays(context.regulation, context.foaPart, context.procedureType);
  const legalBasis = tenderDeadlineProvision(context.regulation, context.foaPart, context.procedureType);
  const deadline =
    days != null && legalBasis != null
      ? `Tilbudsfristen må være minst ${days} dager fra invitasjonen ble sendt (${legalBasis}).`
      : "Det er ingen fast lovbestemt minstefrist for tilbud å oppgi for denne konkurransen.";
  return {
    kind: "dpsCompetition",
    regulation: context.regulation ?? null,
    part: context.foaPart ?? null,
    minimumTenderDays: days,
    legalBasis,
    note:
      "Dette er en konkurranse under en dynamisk innkjøpsordning. Prosedyren er gitt av ordningen: alle leverandører " +
      "som er tatt opp i ordningen, inviteres, og det er ikke noe eget prosedyrevalg. Nevn ikke andre prosedyrer. " +
      deadline,
  };
};

const MISSING_INPUT_WORDS: Record<MissingInput, string> = {
  estimatedValue: "estimert verdi",
  contractNature: "kontraktstype (vare, tjeneste eller bygg og anlegg)",
  regulation: "regelverk",
  buyerLegalType: "type oppdragsgiver",
  classification: "bekreftet klassifisering",
};

const missingBeforeClassification = (
  context: Extract<SourcingEventDto["eventContext"], { type: "Unclassified" }>,
): MissingInput[] => {
  const missing: MissingInput[] = [];
  if (context.estimatedValue?.value == null) missing.push("estimatedValue");
  if (context.contractNature == null) missing.push("contractNature");
  return missing;
};

const unclassifiedNote = (missing: MissingInput[]): string =>
  missing.length === 0
    ? "Anskaffelsen er ikke klassifisert ennå. Brukeren må bekrefte klassifiseringen i veiviseren før regelverk og prosedyrer kan avgjøres."
    : `Regelverk, prosedyrer og minstefrister kan ikke avgjøres ennå, fordi anskaffelsen mangler: ${missing
        .map((input) => MISSING_INPUT_WORDS[input])
        .join(", ")}. Spør brukeren om dette, i denne rekkefølgen. Ikke foreslå å bekrefte klassifiseringen før det er fylt inn.`;

const missingInputs = (context: Extract<SourcingEventDto["eventContext"], { type: "Public" }>): MissingInput[] => {
  if (context.foaPart) return [];
  const missing: MissingInput[] = [];
  if (context.estimatedValue?.value == null) missing.push("estimatedValue");
  if (context.regulation == null) missing.push("regulation");
  if (context.contractNature == null) missing.push("contractNature");
  if (
    context.buyerLegalType == null &&
    context.regulation != null &&
    context.contractNature != null &&
    needsBuyerLegalType(context.regulation, context.contractNature)
  ) {
    missing.push("buyerLegalType");
  }
  return missing.length > 0 ? missing : ["classification"];
};
